import * as React from 'react';

import { EarlyAccessChip } from 'pages/reports/components/EarlyAccessChip';
import { TruncatedText } from 'components/truncated_text/TruncatedText';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { isInsightReport } from 'pages/reports/utils/insightReports';
import { scopeNameFor } from 'pages/reports/utils/scopePolicy';
import { CategoryReport, Report } from 'apis/ReportApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { PageHeader } from 'components/layout/page';
import { helpContent } from 'pages/reports/help';
import { Card, Col, Row } from 'react-bootstrap';
import { useSuperState } from 'hooks';

import 'pages/reports/components/ReportsLanding/ReportsLanding.scss';

interface ReportsLandingProps {

    /** Exposes the confirmRef dialog used for delete confirmation. */
    confirmRef: React.MutableRefObject<any>;
    customReports: Report[];

    /** When true, a small ScopeId badge is shown next to each report name. */
    isSuperAdmin?: boolean;
    listOfReports: CategoryReport[];

    /** Called after a successful delete so the parent can refresh its catalog. */
    onCustomReportDeleted: () => void | Promise<void>;
    onCustomReportSelect: (report: any) => void;
    onEditCustomReport: (report: any) => void;
    onReportSelect: (report: Report) => void;
}

export const ReportsLanding = (props: ReportsLandingProps) => {

    const {
        isSuperAdmin,
        listOfReports,
        onReportSelect
    } = props;

    const { formatMessage } = useIntl();

    // Only show categories that have at least one report the user can view —
    // an empty category should not produce a tab.
    const visibleCategories = listOfReports.filter(category => category.Reports.length > 0);

    // Restore the tab the user was on before opening a report so the Back
    // button returns them to the same category tab instead of the first one.
    const savedCategoryId = sessionStorage.getItem('reportsSelectedCategoryId');
    const initialTab = savedCategoryId
        ? Math.max(0, visibleCategories.findIndex(category => String(category.CategoryId) === savedCategoryId))
        : 0;

    const [state, setState] = useSuperState({
        selectedTab: initialTab
    });

    const { selectedTab } = state;

    return (
        <div >
            <PageHeader helpContent={helpContent} title={formatMessage({ id: 'reports.c01b78b6', defaultMessage: 'Reports' })} />
            <div className="tab-page-container__content">

                <GMetrixTabs
                    onSelect={index => {

                        setState({ selectedTab: index });
                        const category = visibleCategories[index];
                        if (category) {

                            sessionStorage.setItem('reportsSelectedCategoryId', String(category.CategoryId));

                        }

                    }}
                    selected={selectedTab}
                >
                    {visibleCategories.map(category => (
                        <GMetrixTabsTab key={category.CategoryId} title={category.Category}>
                            <Row className="g-3" lg={3} md={2} sm={1} xs={1}>
                                {category.Reports.map((report, index) => (
                                    <Col key={report.Id}>
                                        <Card
                                            className="shadow-sm border rounded-3 h-100 reports-landing__card"
                                            style={{ animationDelay: `${index * 50}ms` }}
                                        >
                                            <Card.Header className="d-flex justify-content-between align-items-center bg-white fw-bold fs-5 py-3">
                                                <TruncatedText className="reports-landing__title">
                                                    {report.Name}
                                                </TruncatedText>
                                                <span className="d-flex align-items-center gap-1 ms-2 flex-shrink-0">
                                                    {isInsightReport(report.Id) && (
                                                        <EarlyAccessChip id={`early-access-card-${report.Id}`} />
                                                    )}
                                                    {isSuperAdmin && (
                                                        <>
                                                            <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                                                                {report.Id}
                                                            </span>
                                                            <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                                                                {scopeNameFor(report.ScopeId)}
                                                            </span>
                                                        </>
                                                    )}
                                                </span>
                                            </Card.Header>
                                            <Card.Body className="pt-3">
                                                <Card.Text className="text-muted small">
                                                    <span dangerouslySetInnerHTML={{ __html: report.Description }} />
                                                </Card.Text>
                                            </Card.Body>
                                            <div className="m-3 mt-0 d-flex justify-content-end">
                                                <GMetrixButton
                                                    className="reports-landing__view-button"
                                                    onClick={() => onReportSelect(report)}
                                                    size="sm"
                                                    variant="outline-primary"
                                                >
                                                    {<FormattedMessage defaultMessage="View" id="view.1590505b" />}
                                                </GMetrixButton>
                                            </div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </GMetrixTabsTab>
                    ))}
                </GMetrixTabs>

            </div>
        </div>
    );

};
